/**
 * EPI Service
 * API integration for EPI (Equipamentos de Proteção Individual) CRUD operations
 *
 * @version 2025.2.0
 * @description Serviço de integração com API de EPIs (entidade global)
 */
import apiClient from './apiClient';
import {
  Epi,
  EpiFilters,
  EpiFormData,
  EpiApiPayload,
  EpiListResponse,
  EpiDependencies,
  EpiCaAlerts,
  EpiStatus,
  TipoDeRisco,
} from '../types/epi';

// Re-export types for convenience
export * from '../types/epi';

export interface EpiListParams extends EpiFilters {
  page?: number;
  limit?: number;
}

export type { UnresolvedTerm, AvailableOption, ImportPreviewResult } from '../types/import';
import type { ImportPreviewResult } from '../types/import';

export interface ImportMappings {
  tipo_de_risco?: Record<string, string>;
}

class EpiService {
  private baseUrl = '/epis';

  /**
   * Convert form data to API payload
   */
  private formDataToApiPayload(formData: EpiFormData): EpiApiPayload {
    return {
      nome: formData.nome,
      tipoDeRisco: formData.tipoDeRisco,
      risco: formData.risco,
      eSocial: formData.eSocial || undefined,
      ativo: formData.ativo,
      inicioValidade: formData.inicioValidade || undefined,
      fimValidade: formData.fimValidade,
      atenuacao: formData.atenuacao || undefined,
      efficiency: formData.efficiency || undefined,
      brandModel: formData.brandModel || undefined,
      metodo: formData.metodo,
      custo: formData.custo || undefined,
      quantidade: formData.quantidade || undefined,
      unidade: formData.unidade || undefined,
      tamanho: formData.tamanho || undefined,
      observacao: formData.observacao || undefined,
      orientacaoDeUso: formData.orientacaoDeUso || null,
    };
  }

  /**
   * Convert API response to form data
   */
  apiResponseToFormData(apiData: Epi): EpiFormData {
    return {
      nome: apiData.nome,
      tipoDeRisco: apiData.tipoDeRisco,
      risco: apiData.risco,
      eSocial: apiData.eSocial || undefined,
      ativo: apiData.ativo,
      inicioValidade: apiData.inicioValidade || undefined,
      fimValidade: apiData.fimValidade,
      atenuacao: apiData.atenuacao || undefined,
      efficiency: apiData.efficiency || undefined,
      brandModel: apiData.brandModel || undefined,
      metodo: apiData.metodo,
      custo: apiData.custo || undefined,
      quantidade: apiData.quantidade || undefined,
      unidade: apiData.unidade || undefined,
      tamanho: apiData.tamanho || undefined,
      observacao: apiData.observacao || undefined,
      orientacaoDeUso: apiData.orientacaoDeUso || undefined,
    };
  }

  /**
   * Get paginated list of EPIs
   */
  async getEpis(params: EpiListParams = {}): Promise<EpiListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.tipoDeRisco) queryParams.append('tipoDeRisco', filters.tipoDeRisco);
    if (filters.ativo) queryParams.append('ativo', filters.ativo);
    if (filters.caExpired !== undefined) queryParams.append('caExpired', String(filters.caExpired));
    if (filters.caExpiringSoon !== undefined) queryParams.append('caExpiringSoon', String(filters.caExpiringSoon));
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { epis, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      epis: backendData.epis ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get EPI by ID
   */
  async getEpiById(id: string | number): Promise<Epi> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new EPI
   */
  async createEpi(data: EpiFormData): Promise<Epi> {
    const payload = this.formDataToApiPayload(data);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data || response.data;
  }

  /**
   * Update EPI by ID
   */
  async updateEpi(id: string | number, data: Partial<EpiFormData>): Promise<Epi> {
    const payload = this.formDataToApiPayload(data as EpiFormData);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data || response.data;
  }

  /**
   * Delete EPI by ID (soft delete)
   */
  async deleteEpi(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate EPI
   */
  async activateEpi(id: string | number): Promise<Epi> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/activate`);
    return response.data.data || response.data;
  }

  /**
   * Deactivate EPI
   */
  async deactivateEpi(id: string | number): Promise<Epi> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Get EPIs with expired CA
   */
  async getExpiredCa(): Promise<Epi[]> {
    const response = await apiClient.get(`${this.baseUrl}/expired`);
    return response.data.data || response.data;
  }

  /**
   * Get EPIs with CA expiring soon
   */
  async getCaExpiringSoon(days: number = 30): Promise<Epi[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('days', days.toString());

    const response = await apiClient.get(`${this.baseUrl}/expiring-soon?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Get CA alerts (expired and expiring soon)
   */
  async getCaAlerts(days: number = 30): Promise<EpiCaAlerts> {
    const queryParams = new URLSearchParams();
    queryParams.append('days', days.toString());

    const response = await apiClient.get(`${this.baseUrl}/alerts?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Check dependencies before deletion
   */
  async checkDependencies(id: string | number): Promise<EpiDependencies> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }

  /**
   * Download import template
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Preview import EPIs from Excel (returns unresolved terms for mapping)
   */
  async previewImport(file: File): Promise<ImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Import EPIs from Excel
   */
  async importEpis(file: File, options?: { mappings?: ImportMappings }): Promise<{ imported: number; skipped?: number; errors: Array<{ row: number; error: string }> }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.mappings) formData.append('mappings', JSON.stringify(options.mappings));
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Export EPIs to Excel
   */
  async exportEpis(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/export`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Get TipoDeRisco options for select dropdowns
   */
  getTipoDeRiscoOptions(): Array<{ value: TipoDeRisco; label: string }> {
    return [
      { value: TipoDeRisco.FISICO, label: 'Físico' },
      { value: TipoDeRisco.QUIMICO, label: 'Químico' },
      { value: TipoDeRisco.BIOLOGICO, label: 'Biológico' },
      { value: TipoDeRisco.ERGONOMICO, label: 'Ergonômico' },
      { value: TipoDeRisco.ACIDENTE, label: 'Acidente' },
    ];
  }

  /**
   * Get EpiStatus options for select dropdowns
   */
  getStatusOptions(): Array<{ value: EpiStatus; label: string }> {
    return [
      { value: EpiStatus.ATIVO, label: 'Ativo' },
      { value: EpiStatus.INATIVO, label: 'Inativo' },
    ];
  }
}

// Export singleton instance
export const epiService = new EpiService();
export default epiService;
